class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
    this.addEventListener('click', (event) => this.addCovrixUpsell(event));
    this.cvMediaObserver = new MutationObserver(() => {
      if (this.classList.contains('active')) this.loadCovrixExtras();
    });
    this.cvMediaObserver.observe(this, {childList: true, subtree: true});
  }

  async addCovrixUpsell(event) {
    const button = event.target.closest('[data-cv-upsell]');
    if (!button || this.cvAdding) return;
    const card = button.closest('.cv-upsell');
    const select = card.querySelector('select');
    const option = select?.selectedOptions[0];
    const variantId = Number(option?.value || button.dataset.variant);
    const quantity = Math.max(1, Number(option?.dataset.min || button.dataset.quantity || 1));
    this.cvAdding = true;
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    card.querySelector('.cv-upsell-error').textContent = '';
    this.querySelectorAll('.quantity__button, .quantity__input, cart-remove-button button, #CartDrawer-Checkout').forEach(el => { if (!el.disabled) { el.dataset.cvDisabled = 'true'; el.disabled = true; } });
    try {
      const response = await fetch(routes.cart_add_url, {
        method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: variantId, quantity }], sections: this.getSectionsToRender().map(s => s.id), sections_url: window.location.pathname })
      });
      const state = await response.json();
      if (!response.ok || state.status) throw new Error(state.description || button.dataset.error);
      if (!state.sections?.['cart-drawer']) {
        const rendered = await fetch(`${routes.cart_url}?sections=cart-drawer,cart-icon-bubble`);
        state.sections = await rendered.json();
      }
      this.renderContents(state);
    } catch (error) {
      card.querySelector('.cv-upsell-error').textContent = error.message || button.dataset.error;
    } finally {
      this.cvAdding = false;
      button.disabled = false;
      button.removeAttribute('aria-busy');
      this.querySelectorAll('[data-cv-disabled]').forEach(el => { el.disabled = false; delete el.dataset.cvDisabled; });
    }
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  loadCovrixExtras() {
    this.querySelectorAll('img[data-cv-cart-src]').forEach(image => {
      if (image.dataset.cvCartSrcset) { image.srcset = image.dataset.cvCartSrcset; delete image.dataset.cvCartSrcset; }
      image.src = image.dataset.cvCartSrc; delete image.dataset.cvCartSrc;
    });
    if (this.cvExtrasLoading) return;
    const scripts = [...document.querySelectorAll('template[data-cv-cart-script]')];
    this.cvExtrasLoading = Promise.all(scripts.map(template => new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = template.dataset.cvCartScript;
      script.onload = () => { template.remove(); resolve(); };
      script.onerror = () => { script.remove(); reject(new Error('Cart extras temporarily unavailable')); };
      document.head.append(script);
    }))).catch(error => { this.cvExtrasLoading = null; console.warn(error.message); });
  }

  open(triggeredBy) {
    this.loadCovrixExtras();
    if (this.classList.contains('active')) return;
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true },
    );

    document.body.classList.add('overflow-hidden');

    // cart-drawer-items is a CartItems subclass that extends createViewEventElement.
    // Its `view-event-trigger="manual"` skips auto-dispatch on connect; we fire
    // it here when the drawer opens, with `context: 'dialog'` from the payload attribute.
    this.querySelector('cart-drawer-items')?.dispatchViewEvent();
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState, shouldOpen = true) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    if (this.classList.contains('active')) this.loadCovrixExtras();
    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.classList.toggle('is-empty', Boolean(this.querySelector('.drawer__inner-empty')));
      if (shouldOpen) {
        if (this.classList.contains('active')) trapFocus(this.querySelector('.drawer__inner'), this.querySelector('.drawer__close'));
        else this.open();
      }
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  onCartUpdate() {
    return fetch(`${routes.cart_url}?sections=cart-drawer,cart-icon-bubble`).then(r => r.json()).then(sections => {
      const drawer = document.querySelector('cart-drawer');
      const wasOpen = drawer.classList.contains('active');
      drawer.renderContents({ sections }, wasOpen);
    }).catch(console.error);
  }
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

document.addEventListener('submit', async (event) => {
  const form = event.target;
  if (!(form instanceof HTMLFormElement) || !form.querySelector('.phg-atc') || event.defaultPrevented) return;
  const drawer = document.querySelector('cart-drawer');
  if (!drawer) return;
  event.preventDefault();
  if (form.dataset.cvSubmitting) return;
  form.dataset.cvSubmitting = 'true';
  const button = form.querySelector('.phg-atc');
  const body = new FormData(form);
  body.set('sections', drawer.getSectionsToRender().map(s => s.id).join(','));
  body.set('sections_url', window.location.pathname);
  button.disabled = true;
  button.setAttribute('aria-busy', 'true');
  let errorNode = form.querySelector('.cv-product-cart-error');
  if (!errorNode) {
    errorNode = document.createElement('p');
    errorNode.className = 'cv-product-cart-error';
    errorNode.setAttribute('role', 'alert');
    button.after(errorNode);
  }
  errorNode.textContent = '';
  try {
    const response = await fetch(routes.cart_add_url, { method: 'POST', headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' }, body });
    const state = await response.json();
    if (!response.ok || state.status) throw new Error(state.description || window.cartStrings.error);
    if (!state.sections?.['cart-drawer']) {
      const rendered = await fetch(`${routes.cart_url}?sections=cart-drawer,cart-icon-bubble`);
      state.sections = await rendered.json();
    }
    drawer.setActiveElement(button);
    drawer.renderContents(state);
  } catch (error) {
    errorNode.textContent = error.message || window.cartStrings.error;
  } finally {
    delete form.dataset.cvSubmitting;
    button.disabled = false;
    button.removeAttribute('aria-busy');
  }
});